import React, { useEffect, useState } from 'react';
import SafeIcon from './common/SafeIcon';
import { DEFAULT_SETTINGS, INITIAL_DATA, INITIAL_TEMPLATES } from './data/constants';
import SettingsPanel from './components/SettingsPanel';
import ProblemCard from './components/ProblemCard';
import SummaryCard from './components/SummaryCard';
import TemplateEditor from './components/TemplateEditor';
import TemplateLibrary from './components/TemplateLibrary';
import PrintReport from './components/PrintReport';
import './index.css';

const TEMPLATES_STORAGE_KEY = 'moldtech-roi-rechner-templates';

const createId = (prefix) =>
  `${prefix}_${Date.now()}${Math.random().toString(36).slice(2, 8)}`;

const createTemplateEfforts = (template) => {
  const templateEfforts =
    template.efforts?.map((effort) => ({
      ...effort,
      id: createId('effort')
    })) || [];

  if (templateEfforts.length === 0) {
    return [
      {
        id: createId('effort'),
        name: template.name,
        freq: '',
        freqUnit: 'week',
        time: '',
        timeUnit: 'min'
      }
    ];
  }

  return templateEfforts.map((effort, index) =>
    index === 0 ? { ...effort, name: template.name } : effort
  );
};

const loadTemplates = () => {
  try {
    const storedTemplates = window.localStorage.getItem(TEMPLATES_STORAGE_KEY);

    if (!storedTemplates) {
      return INITIAL_TEMPLATES;
    }

    const parsedTemplates = JSON.parse(storedTemplates);
    return Array.isArray(parsedTemplates) ? parsedTemplates : INITIAL_TEMPLATES;
  } catch {
    return INITIAL_TEMPLATES;
  }
};

function App() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [problems, setProblems] = useState(INITIAL_DATA);
  const [templates, setTemplates] = useState(loadTemplates);
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(
      TEMPLATES_STORAGE_KEY,
      JSON.stringify(templates)
    );
  }, [templates]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (problems.length > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [problems.length]);

  const addProblem = (template = null) => {
    const efforts = template ? createTemplateEfforts(template) : [];
    const newProblem = {
      id: createId('problem'),
      name: template?.name || '',
      efforts,
      focusEffortId: template ? efforts[0]?.id : null
    };

    setProblems((currentProblems) => [...currentProblems, newProblem]);
  };

  const handleTemplateDrop = (event) => {
    event.preventDefault();

    const templateId = event.dataTransfer.getData('template-id');
    const template = templates.find((item) => item.id === templateId);

    if (template) {
      addProblem(template);
    }
  };

  const updateProblem = (id, updates) => {
    setProblems((currentProblems) =>
      currentProblems.map((problem) =>
        problem.id === id ? { ...problem, ...updates } : problem
      )
    );
  };

  const removeProblem = (id) => {
    setProblems((currentProblems) =>
      currentProblems.filter((problem) => problem.id !== id)
    );
  };

  const handlePrint = () => window.print();

  return (
    <>
      <div className="screen-app min-h-screen bg-gray-50 text-gray-800 font-sans pb-20">
        <header className="bg-white sticky top-0 z-30 shadow-sm">
          <div className="border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img
                  src="https://media-manager-c.questera.ai/greta-media/116a3eb51ffbcf2931cfd2bd07e79bf6b5f522eb2cb1a99b910065fd25fbaf3eb1d96bb3349b02209ad422f53caeedc3/images/aW1hZ2UvcG5n/5a429034d28f17c08328ff943da9fd8a.png"
                  alt="moldtech Logo"
                  className="h-10 w-auto rounded-full"
                />
                <div className="border-l border-gray-300 pl-4">
                  <h1 className="text-xl font-bold text-gray-900">
                    moldtech ROI Rechner
                  </h1>
                  <p className="text-xs text-gray-500">
                    Wieviel Gewinn schneiden Sie täglich weg?
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsTemplateEditorOpen(true)}
                  className="flex items-center gap-2 border border-gray-300 hover:border-gray-900 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                >
                  <SafeIcon name="Settings" />
                  Vorlagen-Einstellungen
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-gray-900 hover:bg-black text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all"
                >
                  <SafeIcon name="Download" />
                  Als PDF exportieren
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          <SettingsPanel settings={settings} setSettings={setSettings} />
          <TemplateLibrary
            templates={templates}
            onTemplateClick={addProblem}
          />

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-2/3">
              <div className="flex items-center justify-between mb-6 border-b border-gray-300 pb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  Analysierte Problemstellung
                </h3>

                {problems.length > 0 && (
                  <button
                    onClick={() => addProblem()}
                    className="inline-flex items-center gap-2 text-sm font-bold text-red-600 hover:text-red-700 transition-colors"
                  >
                    <SafeIcon name="PlusCircle" />
                    Aufwand hinzufügen
                  </button>
                )}
              </div>

              <div
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleTemplateDrop}
                className="space-y-6"
              >
                {problems.length === 0 ? (
                  <button
                    onClick={() => addProblem()}
                    className="w-full min-h-[280px] bg-white rounded-2xl border-2 border-dashed border-gray-300 hover:border-red-400 hover:bg-red-50/30 flex flex-col items-center justify-center text-gray-300 hover:text-red-600 transition-all group"
                    aria-label="Ersten Aufwand hinzufügen"
                  >
                    <span className="text-8xl font-light leading-none group-hover:scale-110 transition-transform">
                      +
                    </span>
                    <span className="mt-4 text-sm font-bold text-gray-500 group-hover:text-red-600">
                      Ersten Aufwand hinzufügen
                    </span>
                  </button>
                ) : (
                  problems.map((problem) => (
                    <ProblemCard
                      key={problem.id}
                      problem={problem}
                      settings={settings}
                      updateProblem={updateProblem}
                      removeProblem={removeProblem}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="w-full lg:w-1/3">
              <SummaryCard problems={problems} settings={settings} />
            </div>
          </div>
        </main>

        {isTemplateEditorOpen && (
          <TemplateEditor
            templates={templates}
            setTemplates={setTemplates}
            onClose={() => setIsTemplateEditorOpen(false)}
          />
        )}
      </div>

      <PrintReport problems={problems} settings={settings} />
    </>
  );
}

export default App;